import { Box, Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { StaffingChangeSummary } from '../operations/model';
import {
  processLabels,
  statusPresentation,
  type StaffingRow,
  type StatusFilter,
} from '../model/staffingPagePresentation';
import {
  IdentityAvatar,
  ResourceCollection,
  StatusBadge,
  TableIdentityCell,
  TableTextCell,
  ValueBadge,
  type ResourceCollectionState,
} from '../../../design-system';

export type StaffingChangesTableProps = {
  rows: StaffingRow[];
  totalCount: number;
  state: ResourceCollectionState;
  compact: boolean;
  today: string;
  query: string;
  processFilter: string;
  statusFilter: StatusFilter;
  onQueryChange: (value: string) => void;
  onProcessFilterChange: (value: string) => void;
  onStatusFilterChange: (value: StatusFilter) => void;
  onRowClick: (change: StaffingChangeSummary) => void;
};

export function StaffingChangesTable({
  rows,
  totalCount,
  state,
  compact,
  today,
  query,
  processFilter,
  statusFilter,
  onQueryChange,
  onProcessFilterChange,
  onStatusFilterChange,
  onRowClick,
}: StaffingChangesTableProps) {
  const columns: GridColDef<StaffingRow>[] = [
    {
      field: 'worker', headerName: '대상 구성원', minWidth: compact ? 145 : 180, flex: 0.9,
      renderCell: ({ row }) => <TableIdentityCell primary={row.worker} secondary={row.workerNumber} leading={<IdentityAvatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 750 }}>{Array.from(row.worker)[0]}</IdentityAvatar>} />,
    },
    {
      field: 'process', headerName: '발령 내용', minWidth: compact ? 165 : 200, flex: 1.15,
      renderCell: ({ row }) => {
        const presentation = statusPresentation(row.source, today);
        return <Box sx={{ minWidth: 0, width: '100%' }}>
          <ValueBadge label={row.process} />
          <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 0.25 }}><TableTextCell variant="caption" color="text.secondary" lines={2}>{row.summary}</TableTextCell></Box>
          <Stack direction="row" spacing={0.75} sx={{ display: { xs: 'flex', md: 'none' }, mt: 0.5, alignItems: 'center', minWidth: 0 }}>
            <StatusBadge label={presentation.label} tone={presentation.tone} />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{row.effectiveDate}</Typography>
          </Stack>
        </Box>;
      },
    },
    { field: 'effectiveDate', headerName: '시행일', width: 125 },
    {
      field: 'status', headerName: '상태', width: 120,
      renderCell: ({ row }) => { const presentation = statusPresentation(row.source, today); return <StatusBadge label={presentation.label} tone={presentation.tone} />; },
    },
    {
      field: 'activityDate', headerName: '최근 활동', width: 125,
      renderCell: ({ row }) => <Stack spacing={0.15} sx={{ justifyContent: 'center', height: '100%' }}><Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15 }}>{row.activityLabel}</Typography><Typography variant="body2" sx={{ lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{row.activityDate}</Typography></Stack>,
    },
  ];

  return <ResourceCollection
    state={state}
    rows={rows}
    totalCount={totalCount}
    columns={columns}
    columnVisibilityModel={{ effectiveDate: !compact, status: !compact, activityDate: !compact }}
    query={query}
    onQueryChange={onQueryChange}
    searchPlaceholder="구성원, 발령 유형 또는 변경 항목 검색"
    ariaLabel="인사 발령 목록"
    filters={[
      { label: '발령 유형', value: processFilter, onChange: onProcessFilterChange, options: [{ label: '전체', value: '' }, ...Object.entries(processLabels).map(([value, label]) => ({ value, label }))] },
      { label: '상태', value: statusFilter, onChange: (value) => onStatusFilterChange(value as StatusFilter), options: [{ label: '전체', value: '' }, { label: '초안', value: 'DRAFT' }, { label: '승인 중', value: 'IN_PROGRESS' }, { label: '시행 대기', value: 'SCHEDULED' }, { label: '완료', value: 'COMPLETED' }, { label: '반려', value: 'DENIED' }, { label: '취소', value: 'CANCELLED' }] },
    ]}
    rowHeight={compact ? 68 : 76}
    onRowClick={({ row }) => onRowClick(row.source)}
  />;
}
